import {
  GearOutlineIcon,
  QuestionMarkOutlineIcon,
  AccountOutlineIcon,
  DataSharingOutlinedIcon,
} from "@tandemdiabetes/sugarmate-react-components"
import { Link } from "@tanstack/react-router"
import classNames from "classnames"
import {
  forwardRef,
  useCallback,
} from "react"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"

import { AccountSummaryLink } from "./AccountSummaryLink"
import styles from "./NavigationMenu.module.scss"
import { useNavigationMenuContext } from "./useNavigationMenuContext"

import {
  useFollowedUsers,
  usePolyglot,
} from "src/contexts"
import { followToSharer } from "src/utils"

const polyglotPrefix = "components.navigation_menu."
type Props = ComponentPropsWithoutRef<"nav">

export const NavigationMenuNav = forwardRef<HTMLElement, Props>(
  function NavigationMenuNav(props: Props, ref: Ref<HTMLElement>) {
    const {
      currentUserId,
      selfFollow,
      currentFollow,
      follows,
      isFollowOnlyWithNoFollows,
    } = useFollowedUsers()

    const className = classNames(
      styles.nav,
      props.className,
    )

    const { setOpen } = useNavigationMenuContext()
    const polyglot = usePolyglot()

    const closeMenu = useCallback(
      () => {
        setOpen(false)
      },
      [setOpen],
    )

    const displayName = isFollowOnlyWithNoFollows
      ? polyglot.t(`${polyglotPrefix}menu_item_unavailable`)
      : followToSharer(currentFollow)

    return (
      <nav
        {...props}
        className={className}
        ref={ref}
      >
        <div className={styles.usersList}>
          {selfFollow && !selfFollow.is_follow_only && (
            <>
              <div className={styles.accountsHeading}>
                {polyglot.t(`${polyglotPrefix}my_account`)}
              </div>
              <AccountSummaryLink
                follow={selfFollow}
                onClick={closeMenu}
              />
            </>
          )}
          {follows.length > 1 && (
            <>
              <div className={styles.accountsHeading}>
                {polyglot.t(`${polyglotPrefix}following`)}
              </div>
              {follows.map((follow) => {
                if (follow.is_self) {
                  return null
                }

                return (
                  <AccountSummaryLink
                    key={follow.id}
                    follow={follow}
                    onClick={closeMenu}
                  />
                )
              })}
            </>
          )}
        </div>
        <div className={styles.navigationList}>
          <Link
            to={currentFollow.is_self ? "/settings" : "/settings/$accountId"}
            params={currentFollow.is_self ? {} : { accountId: currentUserId }}
            id="settings"
            className={styles.link}
            onClick={closeMenu}
          >
            <GearOutlineIcon className={styles.linkIcon} />
            <div className={styles.usernameContainer}>
              {polyglot.t(`${polyglotPrefix}settings`)}
              <div className={styles.username}>
                {displayName}
              </div>
            </div>
          </Link>
          <Link
            to="/data_sharing"
            id="data-sharing"
            className={styles.link}
            onClick={closeMenu}
          >
            <DataSharingOutlinedIcon className={styles.linkIcon} />
            {polyglot.t(`${polyglotPrefix}data_sharing`)}
          </Link>
          <Link
            to="/account"
            id="my-account-btn"
            className={styles.link}
            onClick={closeMenu}
          >
            <AccountOutlineIcon className={styles.linkIcon} />
            {polyglot.t(`${polyglotPrefix}my_account`)}
          </Link>
          <Link
            to="/help_and_feedback"
            id="help-and-feedback"
            className={styles.link}
            onClick={closeMenu}
          >
            <QuestionMarkOutlineIcon className={styles.linkIcon} />
            {polyglot.t(`${polyglotPrefix}help_and_feedback`)}
          </Link>
        </div>
      </nav>
    )
  },
)
